/**
 * KaraokeLyrics
 *
 * Full karaoke panel matching the Sing4You design:
 *   ┌──────────────────────────────────────────────────────┐
 *   │ LYRICS   Bohemian Rhapsody — Queen            [⚙]   │
 *   │                                                      │
 *   │        If I'm not back again this time tomorrow      │  ← inactive (gray)
 *   │  ┌──────────────────────────────────────────────┐   │
 *   │  │  Carry on, carry on as if nothing really     │   │  ← active (golden)
 *   │  │              matters                         │   │
 *   │  └──────────────────────────────────────────────┘   │
 *   │                                                      │
 *   │ 1:12 ████████████████████████████████████ ● 1:12    │
 *   │              [⏮]  [   ▶   ]  [⏭]              🔊   │
 *   │ ▁▃▅▂▄▆▃▅▁▂▃▅▄▂▃▅▁▂                      SCORE: 94  │
 *   └──────────────────────────────────────────────────────┘
 */

import React, { useMemo } from "react";
import { MusicNote, Pause, PlayArrow, SkipNext, SkipPrevious, VolumeUp } from "@mui/icons-material";
import "../../../../styles/sing4you-live.css";

/* ── LRC parser (unchanged) ──────────────────────────────────── */
interface LrcLine {
  time: number;
  text: string;
}

const parseLrc = (lrc: string): LrcLine[] => {
  const entries: LrcLine[] = [];
  for (const rawLine of lrc.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    // Support 1-3 fractional digits: centiseconds [MM:SS.xx] or
    // milliseconds [MM:SS.xxx] — divisor scales with digit count.
    const timeTagRegex = /\[(\d{1,2}):(\d{1,2})(?:\.(\d{1,3}))?]/g;
    let match: RegExpExecArray | null;
    let lastIndex = 0;
    const times: number[] = [];
    while ((match = timeTagRegex.exec(line)) !== null) {
      const min     = parseInt(match[1] ?? "0", 10);
      const sec     = parseInt(match[2] ?? "0", 10);
      const fracStr = match[3] ?? "0";
      const frac    = parseInt(fracStr, 10) / Math.pow(10, fracStr.length);
      times.push(min * 60 + sec + frac);
      lastIndex = match.index + match[0].length;
    }
    if (!times.length) continue;
    const text = line.slice(lastIndex).trim();
    if (!text) continue;
    times.forEach((t) => entries.push({ time: t, text }));
  }
  return entries.sort((a, b) => a.time - b.time);
};

// Removed MIN_HOLD_SECONDS — standard LRC: show the last line whose
// timestamp ≤ currentTime, advance exactly when the next line starts.

const formatTime = (s: number): string => {
  if (!isFinite(s) || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
};

/* ── Waveform bars config ─────────────────────────────────────── */
const WAVE_HEIGHTS = [
  0.30, 0.60, 0.90, 0.50, 0.75, 1.00, 0.40, 0.85, 0.55, 0.30,
  0.70, 0.50, 0.95, 0.40, 0.65, 0.80, 0.35, 0.55, 0.75, 0.90,
  0.45, 0.60, 0.50, 0.80,
];

/* ── Props ───────────────────────────────────────────────────── */
export interface KaraokeLyricsProps {
  /** Parsed .lrc file content */
  lrc: string;
  /** Current playback position in seconds */
  currentTime: number;
  /** Total track duration in seconds */
  duration?: number;
  /** Displayed song title (e.g. "Bohemian Rhapsody — Queen") */
  songTitle?: string;
  /** Karaoke performance score 0–100 */
  score?: number;
  /** Whether audio is currently playing (drives waveform + button icon) */
  isPlaying?: boolean;
  /** Called when the user presses Play/Pause */
  onPlayPause?: () => void;
  /** Called when the user presses Previous */
  onPrev?: () => void;
  /** Called when the user presses Next */
  onNext?: () => void;
  /** Called when the user clicks the settings / karaoke-studio button */
  onSettingsClick?: () => void;
  /** Current volume 0–100 (for the slider display) */
  volume?: number;
  /** Called when user drags the volume slider */
  onVolumeChange?: (v: number) => void;
  /** Called when the user seeks the progress bar (value in seconds) */
  onSeek?: (seconds: number) => void;
}

/* ── Component ───────────────────────────────────────────────── */
const KaraokeLyrics: React.FC<KaraokeLyricsProps> = ({
  lrc,
  currentTime,
  duration = 0,
  songTitle,
  score,
  isPlaying = false,
  onPlayPause,
  onPrev,
  onNext,
  onSettingsClick,
  volume = 80,
  onVolumeChange,
  onSeek,
}) => {
  const parsed = useMemo(() => parseLrc(lrc), [lrc]);

  /* ── Active line calculation ─────────────────────────────────
   * Standard LRC rule: the active line is the last line whose
   * timestamp ≤ currentTime.
   *
   * We default activeIndex to 0 (first line) so it is visible as
   * "upcoming" before the song reaches its timestamp — this keeps
   * the lyrics panel populated as soon as an LRC is loaded, even
   * when currentTime is still 0 (e.g. host page before karaoke_sync
   * arrives, or talent page before the first note plays).
   * ────────────────────────────────────────────────────────── */
  let activeIndex = parsed.length > 0 ? 0 : -1;
  if (parsed.length > 0) {
    const t = Math.max(0, currentTime);
    for (let i = 0; i < parsed.length; i++) {
      if (t >= parsed[i].time) activeIndex = i;
      else break; // lines are sorted; everything after also hasn't started
    }
  }

  const prevLine   = activeIndex > 0                      ? parsed[activeIndex - 1]     : null;
  const activeLine = activeIndex >= 0                      ? parsed[activeIndex]          : null;
  const nextLine   = activeIndex >= 0 && activeIndex < parsed.length - 1
                                                           ? parsed[activeIndex + 1]     : null;

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  // "Waiting" state: lyrics loaded but playback not started yet.
  const isWaiting = parsed.length > 0 && !isPlaying && currentTime === 0;

  return (
    <div className="klrc-panel">

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="klrc-header">
        <span className="klrc-label">Lyrics</span>
        {/* <span className="klrc-song-title">
          {songTitle || "No track loaded"}
        </span> */}
        {isWaiting && (
          <span className="klrc-waiting-badge">Waiting for playback…</span>
        )}
        {onSettingsClick && (
          <button
            className="klrc-settings-btn"
            onClick={onSettingsClick}
            title="Open karaoke studio"
          >
            <MusicNote sx={{ fontSize: "1rem" }} />
          </button>
        )}
      </div>

      {/* ── Lyric display ───────────────────────────────────── */}
      {/* Each line uses a key tied to activeIndex so React remounts
          the element on every line change, triggering the entrance
          CSS animation defined in sing4you-live.css.             */}
      <div className="klrc-display">
        {prevLine && (
          <div key={`prev-${activeIndex}`} className="klrc-line-inactive">
            {prevLine.text}
          </div>
        )}
        {activeLine ? (
          <div key={`active-${activeIndex}`} className="klrc-line-active">
            {activeLine.text}
          </div>
        ) : (
          <div className="klrc-line-empty">Upload a .lrc file to see lyrics</div>
        )}
        {nextLine && (
          <div key={`next-${activeIndex}`} className="klrc-line-next">
            {nextLine.text}
          </div>
        )}
      </div>

      {/* ── Progress bar (interactive when onSeek is provided) ── */}
      <div className="klrc-progress-row">
        <span className="klrc-time">{formatTime(currentTime)}</span>
        <div className="klrc-progress-track" style={{ position: "relative" }}>
          <div
            className="klrc-progress-fill"
            style={{ width: `${progress}%` }}
          />
          {onSeek && duration > 0 && (
            <input
              type="range"
              min={0}
              max={duration}
              step={0.5}
              value={currentTime}
              onChange={(e) => onSeek(Number(e.target.value))}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
                margin: 0,
              }}
              aria-label="Seek"
            />
          )}
        </div>
        <span className="klrc-time">{formatTime(duration || 0)}</span>
      </div>

      {/* ── Playback controls ───────────────────────────────── */}
      <div className="klrc-controls-row">
        {/* Spacer so centre group is truly centred */}
        <div className="klrc-volume-row">
          <span className="klrc-vol-icon">
            <VolumeUp sx={{ fontSize: "1rem" }} />
          </span>
          <input
            type="range"
            className="klrc-vol-slider"
            min={0}
            max={100}
            value={volume}
            onChange={(e) => onVolumeChange?.(Number(e.target.value))}
          />
        </div>

        <div className="klrc-controls-center">
          <button
            className="klrc-pb-btn"
            onClick={onPrev}
            disabled={!onPrev}
            title="Previous"
          >
            <SkipPrevious sx={{ fontSize: "1.6rem" }} />
          </button>

          <button
            className="klrc-pb-play"
            onClick={onPlayPause}
            disabled={!onPlayPause}
            title={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying
              ? <Pause     sx={{ fontSize: "1.4rem" }} />
              : <PlayArrow sx={{ fontSize: "1.4rem" }} />
            }
          </button>

          <button
            className="klrc-pb-btn"
            onClick={onNext}
            disabled={!onNext}
            title="Next"
          >
            <SkipNext sx={{ fontSize: "1.6rem" }} />
          </button>
        </div>

        {/* Right spacer matches left volume row width */}
        <div style={{ minWidth: 80 }} />
      </div>

      {/* ── Bottom row: waveform + score ────────────────────── */}
      <div className="klrc-bottom-row">
        <div className="klrc-waveform" aria-hidden="true">
          {WAVE_HEIGHTS.map((h, i) => (
            <div
              key={i}
              className={`klrc-waveform-bar${isPlaying ? "" : " paused"}`}
              style={{
                height: `${Math.round(h * 14)}px`,
                animationDelay:    `${(i * 0.038).toFixed(3)}s`,
                animationDuration: `${(0.38 + (i % 6) * 0.07).toFixed(2)}s`,
              }}
            />
          ))}
        </div>

        {score !== undefined && (
          <div className="klrc-score">
            SCORE:&nbsp;<span className="klrc-score-value">{score}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default KaraokeLyrics;
